/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CertInfoBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the certificates module.
----------------------------------------------------------------*/


import React from 'react';
import { FileText, CheckCircle, AlertTriangle } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import type { CertificateInfo } from '../types';

interface CertInfoBoxProps {
  info: CertificateInfo;
}

/**
 * CertInfoBox
 *
 * Displays parsed metadata from a certificate (validity dates, issuer,
 * fingerprint, key match). Highlights expiry warnings when fewer than
 * 30 days remain.
 */
export function CertInfoBox({ info }: CertInfoBoxProps) {
  const { t } = useI18n();

  return (
    <FormBox
      title={t('certificates.sections.info')}
      icon={<FileText className="h-5 w-5" />}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center text-sm">
        <span className="text-muted-foreground">{t('certificates.info.commonName')}:</span>
        <span className="font-medium">{info.commonName}</span>

        <span className="text-muted-foreground">{t('certificates.info.issuer')}:</span>
        <span className="font-medium">{info.issuer}</span>

        <span className="text-muted-foreground">{t('certificates.info.validFrom')}:</span>
        <span className="font-medium">
          {info.notBefore ? new Date(info.notBefore).toLocaleDateString() : '�'}
        </span>

        <span className="text-muted-foreground">{t('certificates.info.validUntil')}:</span>
        <span className="font-medium">
          {info.notAfter ? new Date(info.notAfter).toLocaleDateString() : '�'}
          {info.daysUntilExpiry > 0 && (
            <span
              className={`ml-2 text-xs ${
                info.daysUntilExpiry <= 30 ? 'text-yellow-500' : 'text-green-500'
              }`}
            >
              ({info.daysUntilExpiry} {t('certificates.daysLeft')})
            </span>
          )}
        </span>

        {info.fingerprint && (
          <>
            <span className="text-muted-foreground">{t('certificates.info.fingerprint')}:</span>
            <span className="col-span-3 font-mono text-xs break-all">{info.fingerprint}</span>
          </>
        )}

        {info.keyMatch !== undefined && (
          <>
            <span className="text-muted-foreground">Key Status:</span>
            <div className="col-span-3">
              {info.keyMatch ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                  <CheckCircle className="w-3 h-3" />
                  {t('certificates.info.keyMatch')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                  <AlertTriangle className="w-3 h-3" />
                  {t('certificates.info.keyMismatch')}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </FormBox>
  );
}
